import { buildArrowGeometry, type ArrowHead } from './arrowPath';
import { round } from './geometry';
import type { ArrowAnchors } from './placement';

interface OnboardingArrowProps {
  arrow: ArrowAnchors;
}

/** Tip at the origin, pointing along +x; the transform below aims it down the curve. */
function headPoints({ length, width }: ArrowHead): string {
  return `0,0 ${-length},${-width / 2} ${-length},${width / 2}`;
}

/** Scaled with the head so a short hop still shows a few dashes rather than one blob. */
function dashArray({ length }: ArrowHead): string {
  const dash = length * 0.6;
  return `${round(dash, 1)} ${round(dash * 1.22, 1)}`;
}

const STROKE_WIDTH = 3;

/**
 * A white arrow vanishes the moment it crosses the spotlight, where the page shows
 * through undimmed and is usually white itself — which is exactly what happens when
 * a step aims at a link inside the area it highlights. This dark rim keeps the
 * arrow legible on both the dimmed backdrop and the bright cut-out.
 */
const HALO_COLOR = 'rgba(15,23,42,0.5)';
const HALO_WIDTH = STROKE_WIDTH + 3.5;

/**
 * The dashed connector between the step card and the spotlight. Everything is
 * stroked at render size, so the dashes keep their width and the head keeps its
 * shape no matter how long or which way the arrow runs.
 */
export function OnboardingArrow({ arrow }: OnboardingArrowProps) {
  const geometry = buildArrowGeometry(arrow.from, arrow.to, arrow.bow);
  if (!geometry) return null;

  const dashes = dashArray(geometry.head);

  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
      style={{ zIndex: 1 }}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d={geometry.d}
        stroke={HALO_COLOR}
        strokeWidth={HALO_WIDTH}
        strokeDasharray={dashes}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d={geometry.d}
        stroke="white"
        strokeWidth={STROKE_WIDTH}
        strokeDasharray={dashes}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <polygon
        points={headPoints(geometry.head)}
        fill="white"
        stroke={HALO_COLOR}
        strokeWidth={3.5}
        strokeLinejoin="round"
        paintOrder="stroke"
        transform={`translate(${geometry.head.x} ${geometry.head.y}) rotate(${geometry.head.angle})`}
      />
    </svg>
  );
}
